import {Button, Grid, Typography} from '@mui/material'
import React, { type JSX } from 'react';

interface ReportDataSummaryProps {
  visible?: boolean;
  count: number;
  title: string;
  setExpandedItems?: (items: Record<string, unknown>) => void;
  setExpandedAll?: () => void;
  isShowCollapseButton?: boolean;
  handleExpandAll?: () => void;
  /** Whether any items are currently expanded. Drives the Collapse All / Expand All button label. */
  hasExpandedItems?: boolean;
  downloadButtonsSlot?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Displays a summary of the report data with optional expand/collapse controls and download buttons.
 *
 * @param props Component props
 * @param props.visible Whether to display the component (default: true)
 * @param props.count The number of records in the data group
 * @param props.title The title header of the report data summary
 * @param props.setExpandedItems Callback to update expanded items state
 * @param props.setExpandedAll Callback to collapse all items
 * @param props.isShowCollapseButton Whether to show expand/collapse all button (default: false)
 * @param props.handleExpandAll Callback when "Expand All" button is clicked
 * @param props.hasExpandedItems Whether any items are currently expanded
 * @param props.downloadButtonsSlot Slot for download button(s) — always wrap in `DownloadButtonsContainer`
 * @param props.children Additional content to render below
 * @returns JSX.Element | null
 *
 * @example
 * <ReportDataSummary
 *   count={100}
 *   title="All"
 *   isShowCollapseButton={true}
 *   hasExpandedItems={hasExpandedItems}
 *   setExpandedItems={setExpandedItems}
 *   setExpandedAll={setExpandedAll}
 *   handleExpandAll={handleExpandAll}
 *   downloadButtonsSlot={
 *     <DownloadButtonsContainer>
 *       <DownloadCSVButton title='Download Report' reportUrl={downloadUrl} />
 *     </DownloadButtonsContainer>
 *   }
 * >
 *   <Typography variant='body2'>Additional info or controls can go here.</Typography>
 * </ReportDataSummary>
 *
 * @see DownloadButtonsContainer - Wrapper container for {@link DownloadCSVButton}
 */
const ReportDataSummary = ({
                             visible = true,
                             count,
                             title,
                             setExpandedItems,
                             setExpandedAll,
                             isShowCollapseButton = false,
                             handleExpandAll,
                             hasExpandedItems = false,
                             downloadButtonsSlot,
                             children
                           }: ReportDataSummaryProps): JSX.Element | null => {


  const handleCollapseAll = () => {
    setExpandedItems?.({});
    setExpandedAll?.();
  };

  if (!visible) return null;

  return <Grid
    container
    mt={4}
    mb={4}
    sx={{
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 2
    }}
  >
    <Grid item container sx={{alignItems: 'center', gap: 2, flexWrap: 'wrap'}} xs>
      <Typography variant='h6' sx={{textTransform: 'uppercase'}}>
        {title} (SHOWING ALL {count} RECORDS IN GROUP)
      </Typography>
      {isShowCollapseButton &&
        <Button
          onClick={hasExpandedItems ? handleCollapseAll : handleExpandAll}
          variant='text'
        >
          {hasExpandedItems ? 'Collapse All' : 'Expand All'}
        </Button>
      }
    </Grid>
    {downloadButtonsSlot && (
      <Grid item xs={12} md={"auto"} sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        // Makes the DownloadButtonsContainer full-width on small screens.
        // Do NOT go deeper (& > * > *) — DownloadButtonsContainer applies the
        // same rule to its own children, and going deeper would target MUI Button
        // internals (startIcon / label) causing incorrect full-width layout.
        '& > *': {width: {xs: '100%', md: 'auto'}},
      }}>
        {downloadButtonsSlot}
      </Grid>
    )}
    {children}
  </Grid>
}

export default ReportDataSummary;
